import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "@hooks/useTheme";
import VerificationComparisonInfo from "@components/VerificationComparison/VerificationComparisonInfo";
import VerificationComparisonSelector from "@components/VerificationComparison/VerificationComparisonSelector";
import useSbomMerge from "@hooks/useSbomMerge";
import { useMutationData } from "@hooks/useMutationData";
import ComponentListComparisonResult from "@components/VerificationComparison/ComponentListComparisonResult";
import { PrimaryButton } from "@components/Buttons";
import queryKeys from "@configs/react-query/query-key";
import { LOCAL_STORAGE } from "@constants/index";
import { getLocalStorage } from "@utils/local-storage";
import axios from "axios";
import { VERIF_TYPE_OPTIONS } from "@constants/index";
import { basename } from "../../../router";

const VerificationComparison = () => {
  const { t } = useTranslation();
  const { colors } = useTheme();

  const { fetchComponentsForVerificationFiles } = useSbomMerge();


  const [verificationResult1, setVerificationResult1] = useState(null);
  const [selectedVerificationType1, setSelectedVerificationType1] = useState(null);
  const [componentData1, setComponentData1] = useState([]);

  const [verificationResult2, setVerificationResult2] = useState(null);
  const [selectedVerificationType2, setSelectedVerificationType2] = useState(null);
  const [componentData2, setComponentData2] = useState([]);

  const handleSelectVerificationResult1 = (selectedItem) => {
    setVerificationResult1(selectedItem);
    setSelectedVerificationType1(selectedItem.projectPreviewSeq ? "preview" : "project");
    handleGetComponentData(selectedItem, 1);
    // TODO: Load data 1 for comparison
  };

  const handleSelectVerificationResult2 = (selectedItem) => {
    setVerificationResult2(selectedItem);
    setSelectedVerificationType2(selectedItem.projectPreviewSeq ? "preview" : "project");
    handleGetComponentData(selectedItem, 2);
    // TODO: Load data 2 for comparison
  };

  const handleGetComponentData = async (verificationResult, order) => {
    if (verificationResult.projectPreviewSeq) { // preview
      fetchPreviewData.mutate(verificationResult.projectPreviewSeq, {
        onSuccess: (response) => {
          if (typeof response !== "string") {
            if (order === 1) {
              setComponentData1(response);
            } else {
              setComponentData2(response);
            }
          }
        },
        onError: (error) => {
          console.error("Error during get data:", error);
        },
      });
    } else if (verificationResult.projectSeq) { // project
      fetchComponentsForVerificationFiles([verificationResult]).then(components => {
        if (order === 1) {
          setComponentData1(components);
        } else {
          setComponentData2(components);
        }
      });
    }
  }

  const fetchPreviewData = useMutationData(
    (projectPreviewSeq) => `${queryKeys.getProjectPreviewComponentByProjectPreviewSeq}/${projectPreviewSeq}`,
    {
      method: "GET",
    },
  );

  const transformVerificationDataForDownload = (data) => {
    if (!data || typeof data !== 'object') {
      return { verificationType: null, verificationSeq: null };
    }

    const verificationTypeMap = {
      [VERIF_TYPE_OPTIONS[0].label]: "binary",      // Clarity -> binary
      [VERIF_TYPE_OPTIONS[1].label]: "sourceCode",  // FossId -> sourceCode
      [VERIF_TYPE_OPTIONS[2].label]: "sbom",        // SBOM -> sbom
    };
    if (!data.verificationType) {
      return {
        verificationType: "projectPreview",
        verificationSeq: data.projectPreviewSeq || null,
      };
    }

    const verificationType = verificationTypeMap[data.verificationType] ?? data.verificationType;

    return {
      verificationType,
      verificationSeq: data.verificationSeq || null,
    };
  };

  const handleExport = async () => {
    const requestBody = {};

    if (verificationResult1) {
      const result1 = transformVerificationDataForDownload(verificationResult1);
      requestBody.verificationType1 = result1.verificationType;
      requestBody.verificationSeq1 = result1.verificationSeq;
    }

    if (verificationResult2) {
      const result2 = transformVerificationDataForDownload(verificationResult2);
      requestBody.verificationType2 = result2.verificationType;
      requestBody.verificationSeq2 = result2.verificationSeq;
    }
    try {
      const token = getLocalStorage(LOCAL_STORAGE.TOKEN);
      const locale = getLocalStorage("language");
      requestBody.locale = locale;
      const response = await axios(queryKeys.exportVerificationComparison, {
        responseType: "blob",
        method: "GET",
        params: requestBody,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      let part1 = "";
      if (verificationResult1 && verificationResult1) {
        if (verificationResult1.projectPreviewName) {
          part1 = verificationResult1.projectPreviewName;
        } else if (verificationResult1.projectId && verificationResult1.verificationVersion) {
          part1 = `${verificationResult1.projectId}_${verificationResult1.verificationVersion}`;
        } else {
          part1 = "unknown1";
        }
      } else {
        part1 = "unknown1";
      }

      let part2 = "";
      if (verificationResult2 && verificationResult2) {
        if (verificationResult2.projectPreviewName) {
          part2 = verificationResult2.projectPreviewName;
        } else if (verificationResult2.projectId && verificationResult2.verificationVersion) {
          part2 = `${verificationResult2.projectId}_${verificationResult2.verificationVersion}`;
        } else {
          part2 = "unknown2";
        }
      } else {
        part2 = "unknown2";
      }

      let filename = `${part1}_${part2}`;

      if (!filename.toLowerCase().endsWith(".xlsx")) {
        filename += ".xlsx";
      }

      const blob = await response.data;

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (error) {
      console.error("Error downloading file:", error);
    }

  };

  return (
    <div className={`flex h-full flex-col ${colors.bgSecondary}`} data-testid="verification-comparison-page">
      {/* Header */}
      <div className={`${colors.textPlaceholder} mb-4`} data-testid="verification-comparison-header">
        <div className="flex items-center justify-between">
          <h1 className={`text-[28px] uppercase font-bold ${colors.textPrimary}`} data-testid="verification-comparison-title">{t("verification-comparison.title")}</h1>
          <PrimaryButton
            data-testid="verification-comparison-export-button"
            disabled={!verificationResult1 || !verificationResult2}
            onClick={handleExport}
          >
            {t("verification-comparison.export-excel"	)}
          </PrimaryButton>
        </div>
      </div>

      <div className="flex flex-row gap-4 min-w-[960px]" data-testid="verification-comparison-panels-row">
        <div className={`flex-1 min-w-0 ${colors.bgPrimary} ${colors.borderPrimary} border rounded-2xl p-4 lg:p-6`} data-testid="verification-comparison-panel-1">
          {verificationResult1 ? (
            <VerificationComparisonInfo
              type={selectedVerificationType1}
              onSelect={handleSelectVerificationResult1}
              selectedVerificationResult={verificationResult1}
              excludedVerificationResult={verificationResult2}
              componentData={componentData1}
              verificationNumber={1}
              onProjectClick={(projectSeq) => window.open(`${window.location.origin}${basename}/projects/${projectSeq}`, "_blank")}
            />
          ) : (
            <VerificationComparisonSelector
              onSelect={handleSelectVerificationResult1}
              selectedVerificationResult={verificationResult1}
              excludedVerificationResult={verificationResult2}
              verificationNumber={1}
            />
          )}

        </div>

        <div className={`flex-1 min-w-0 ${colors.bgPrimary} ${colors.borderPrimary} border rounded-2xl p-4 lg:p-6`} data-testid="verification-comparison-panel-2">
          {verificationResult2 ? (
            <VerificationComparisonInfo
              type={selectedVerificationType2}
              onSelect={handleSelectVerificationResult2}
              selectedVerificationResult={verificationResult2}
              excludedVerificationResult={verificationResult1}
              componentData={componentData2}
              verificationNumber={2}
              onProjectClick={(projectSeq) => window.open(`${window.location.origin}${basename}/projects/${projectSeq}`, "_blank")}
              />
          ) : (
            <VerificationComparisonSelector
              onSelect={handleSelectVerificationResult2}
              selectedVerificationResult={verificationResult2}
              excludedVerificationResult={verificationResult1}
              verificationNumber={2}
            />
          )}

        </div>
      </div>

      {verificationResult1 && verificationResult2 && (
        <div className={`mt-4 ${colors.bgPrimary} ${colors.borderPrimary} border rounded-2xl p-4 lg:p-6`} data-testid="verification-comparison-result-table">
          <ComponentListComparisonResult
            componentData1={componentData1}
            componentData2={componentData2}
          />
        </div>
      )}

    </div>
  );
};

export default VerificationComparison;